/* eslint-disable no-unused-vars */
import Axios from "axios";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import TextField from "@mui/material/TextField";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import BeenhereIcon from "@mui/icons-material/Beenhere";
import PaymentsIcon from "@mui/icons-material/Payments";
import "./style.css";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button } from "@mui/material";
import deliveryLogo from "../../images/best delivry .png";
import ScrollPage from "../../Components/scrollpageUp";
import slugify from "../../Utils/slugify";
export default function Panier({ onCartChange }) {
  const navigate = useNavigate();

  // =============================
  // CONFIG
  // =============================
  const SHIPPING_COST = 8;

  // Références éligibles à la remise 5% (si qte totale >= 2)
  // ✅ Mets ici les refs concernées par la remise Q&Q
  const REF_REMISE = useMemo(() => new Set([""]), []);

  // ✅ Références éligibles à la LIVRAISON GRATUITE (si au moins 1 produit présent)
  // Mets ici les refs concernées par la livraison gratuite


  // =============================
  // HELPERS
  // =============================
  function isExpired(timestamp) {
    const expirationTime = 5 * 24 * 60 * 60 * 1000;
    return Date.now() - timestamp > expirationTime;
  }

  function formatDate(date) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("fr-FR", options);
  }

  function normalizeRef(ref) {
    return (ref || "").trim().toUpperCase();
  }

  function normalizeCode(c) {
    return (c || "").trim().toUpperCase();
  }

  const safeInt = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };

  // =============================
  // STATE (cart / info)
  // =============================
  const [cart, setCart] = useState(() => {
    const stored = localStorage.getItem("cart");
    return stored ? JSON.parse(stored) : [];
  });

  const [infoString, setInfoString] = useState(() => localStorage.getItem("info"));
  const [infoArray, setInfoArray] = useState([]);

  const [currentDate, setCurrentDate] = useState(new Date());

  const futureDate1 = useMemo(() => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 2);
    return d;
  }, [currentDate]);

  const futureDate2 = useMemo(() => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 4);
    return d;
  }, [currentDate]);

  const [code, setCode] = useState("sans code");

  const [nameValue, setNameValue] = useState("");
  const [adressValue, setAdressValue] = useState("");
  const [NumberValue, setNumberValue] = useState("");

  const [nameError, setNameError] = useState("");
  const [adressError, setAdressError] = useState("");
  const [numberError, setNumberError] = useState("");

  // =============================
  // EFFECTS
  // =============================
  useEffect(() => {
    const interval = setInterval(() => setCurrentDate(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
    localStorage.setItem("nbrPanier", JSON.stringify(cart.length));
  }, [cart]);

  useEffect(() => {
    const str = localStorage.getItem("info");
    setInfoString(str);

    if (!str) {
      setInfoArray([]);
      return;
    }

    try {
      const obj = JSON.parse(str);

      if (obj?.timestamp && isExpired(obj.timestamp)) {
        localStorage.removeItem("info");
        setInfoString(null);
        setInfoArray([]);
        return;
      }

      if (Array.isArray(obj)) setInfoArray(obj);
      else setInfoArray([obj]);
    } catch (e) {
      console.error("Erreur JSON info:", e);
      setInfoArray([]);
    }
  }, []);

  // =============================
  // VALIDATION HANDLERS
  // =============================
  const handleNameChange = (e) => {
    const v = e.target.value;
    setNameValue(v);
    setNameError(v.trim() === "" ? "Ce champ ne peut pas être vide." : "");
  };

  const handleAdressChange = (e) => {
    const v = e.target.value;
    setAdressValue(v);
    setAdressError(v.trim() === "" ? "Ce champ ne peut pas être vide." : "");
  };

  const handleNumberChange = (e) => {
    const v = e.target.value;
    setNumberValue(v);
    const isOk = /^\d{8,}$/.test(v.trim());
    setNumberError(isOk ? "" : "Le téléphone doit contenir au moins 8 chiffres.");
  };

  // =============================
  // CART CALCULATIONS
  // =============================
  const panierQte = useMemo(() => {
    if (!cart || cart.length === 0) return 0;
    return cart.reduce((acc, a) => acc + safeInt(a.quantite), 0);
  }, [cart]);

  const calculerTotal = useCallback(() => {
    if (!cart || cart.length === 0) return 0;
    return cart.reduce((acc, a) => acc + (a.price || 0) * safeInt(a.quantite), 0);
  }, [cart]);

  const calculerQteEligibleRefs = useCallback(() => {
    if (!cart || cart.length === 0) return 0;
    return cart.reduce((acc, a) => {
      const qte = safeInt(a.quantite);
      const ref = normalizeRef(a.reference);
      if (REF_REMISE.has(ref)) return acc + qte;
      return acc;
    }, 0);
  }, [cart, REF_REMISE]);

  const calculerTotalEligibleRefs = useCallback(() => {
    if (!cart || cart.length === 0) return 0;
    return cart.reduce((acc, a) => {
      const qte = safeInt(a.quantite);
      const ref = normalizeRef(a.reference);
      if (REF_REMISE.has(ref)) return acc + (a.price || 0) * qte;
      return acc;
    }, 0);
  }, [cart, REF_REMISE]);

  // Remise 5% uniquement si qte eligible >= 2
  const calculerRemiseRefs = useCallback(() => {
    const qteEligible = calculerQteEligibleRefs();
    const totalEligible = calculerTotalEligibleRefs();
    return qteEligible >= 2 ? totalEligible * 0.05 : 0;
  }, [calculerQteEligibleRefs, calculerTotalEligibleRefs]);

  const calculerSousTotalApresRefs = useCallback(() => {
    return calculerTotal() - calculerRemiseRefs();
  }, [calculerTotal, calculerRemiseRefs]);

  // Remise HELLO-5 : 5% sur articles (après remise refs) si >= 25 (sans livraison)
const calculerRemiseHello5 = useCallback(() => {
  const total = calculerTotal();
  if (normalizeCode(code) === "HELLO-5" && total >= 25) {
    return total * 0.05;
  }
  return 0;
}, [code, calculerTotal]);

  // ✅ Livraison gratuite si au moins 1 article du panier a une référence dans REF_LIVRAISON_GRATUITE
const hasFreeShipping = useMemo(() => {
  if (!cart || cart.length === 0) return false;

  return cart.some((article) => {
    const qte = safeInt(article.quantite);
    return qte > 0 && article.livraisonGratuit === true;
  });
}, [cart]);

  const shippingCost = useMemo(() => {
    return hasFreeShipping ? 0 : SHIPPING_COST;
  }, [hasFreeShipping]);

  // Total final TTC (articles - remises + livraison)
 const calculerTotalFinalAvecLivraison = useCallback(() => {
  const totalArticles = calculerTotal() - calculerRemiseHello5();
  return totalArticles + shippingCost;
}, [calculerTotal, calculerRemiseHello5, shippingCost]);
  // =============================
  // CART ACTIONS
  // =============================
  function deleteArticle(index) {
    setCart((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    if (onCartChange) onCartChange();
  }

  function moin(article, index) {
    setCart((prev) => {
      const updated = prev
        .map((it, i) => {
          if (i !== index) return it;
          const newQuantity = safeInt(it.quantite) - 1;
          return { ...it, quantite: newQuantity };
        })
        .filter((it) => safeInt(it.quantite) > 0);

      return updated;
    });
    if (onCartChange) onCartChange();
  }

  async function plus(article) {
    try {
      const response = await Axios.get(
        `https://ebuy-backend-two.vercel.app/api/article/${article.name}`
      );
      const articleData = response.data;

      setCart((prev) => {
        return prev.map((it) => {
          if (it.name !== article.name) return it;
          const currentQte = safeInt(it.quantite);
          if (currentQte < (articleData.stock || 0)) {
            return { ...it, quantite: currentQte + 1 };
          }
          return it;
        });
      });

      if (onCartChange) onCartChange();
    } catch (error) {
      console.error("Erreur récupération article:", error);
    }
  }

  async function mettreAJourStockEtLocal() {
    try {
      if (!cart || cart.length === 0) return;

      await Promise.all(
        cart.map(async (article) => {
          try {
            const res = await Axios.get(
              `https://ebuy-backend-two.vercel.app/api/article/${article.name}`
            );
            const articleData = res.data;

            const updatedStock = (articleData.stock || 0) - safeInt(article.quantite);
            await Axios.put(
              `https://ebuy-backend-two.vercel.app/api/article/${articleData._id}`,
              { stock: updatedStock }
            );
          } catch (err) {
            console.error("Erreur update stock:", err);
          }
        })
      );
    } catch (error) {
      console.error("Erreur stock global:", error);
    }
  }

  async function passeCommande() {
    if (!cart || cart.length === 0) return;

    if (nameValue.trim() === "") setNameError("Ce champ ne peut pas être vide.");
    if (adressValue.trim() === "") setAdressError("Ce champ ne peut pas être vide.");
    if (!/^\d{8,}$/.test(NumberValue.trim()))
      setNumberError("Le téléphone doit contenir au moins 8 chiffres.");

    if (
      nameValue.trim() === "" ||
      adressValue.trim() === "" ||
      !/^\d{8,}$/.test(NumberValue.trim())
    ) {
      return;
    }

    const commande = cart.map((article) => ({
      quantite: article.quantite,
      name: article.name,
      brand: article.brand,
      ref: article.reference,
      color: article.color,
    }));

    const totalTTC = calculerTotalFinalAvecLivraison().toFixed(3);

    const body = {
      commande,
      total: totalTTC,
      code,
      nom: nameValue,
      adresse: adressValue,
      tel: NumberValue,
      livraison: shippingCost === 0 ? "GRATUITE" : `${shippingCost} TND`,
    };

    try {
      const resp = await fetch("https://ebuy-backend-two.vercel.app/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        throw new Error("Erreur lors de l'envoi de la commande");
      }

      await mettreAJourStockEtLocal();

      localStorage.removeItem("info");
      const infoObject = {
        name: nameValue,
        totalTTC: totalTTC,
        date1: formatDate(futureDate1),
        date2: formatDate(futureDate2),
        adresseLivraison: adressValue,
        telephoneLivraison: NumberValue,
        timestamp: new Date().getTime(),
      };

      localStorage.setItem("info", JSON.stringify(infoObject));
      setInfoString(JSON.stringify(infoObject));
      setInfoArray([infoObject]);

      localStorage.removeItem("cart");
      setCart([]);
      if (onCartChange) onCartChange();
    } catch (error) {
      console.log(error);
    }
  }

  // =============================
  // RENDER
  // =============================
  return (
    <div className="cart">
      <ScrollPage />

      {!cart || cart.length === 0 ? (
        infoString ? (
          <div className="panierVide">
            {infoArray &&
              Array.isArray(infoArray) &&
              infoArray.map((infos, index) => (
                <div key={index}>
                  <p style={{ color: "#F77171", fontWeight: "bold" }}>
                    Votre commande est validée! Merci d&apos;avoir choisi Ebuy. À la prochaine!
                  </p>

                  <p>
                    <Button sx={{ color: "black" }} endIcon={<BeenhereIcon fontSize="small" />}>
                      info livraison :&nbsp;&nbsp;
                    </Button>
                    <p>
                      <span>{infos.name}</span> {" / "} <span>{infos.adresseLivraison}</span>
                    </p>
                    <p>
                      <Button
                        sx={{ color: "black" }}
                        startIcon={<PhoneAndroidIcon fontSize="small" />}
                      >
                        {infos.telephoneLivraison}
                      </Button>
                    </p>
                  </p>

                  <p>
                    <Button sx={{ color: "black" }} startIcon={<LocalShippingIcon />}>
                      Livraison à domicile :&nbsp;&nbsp;
                    </Button>
                  </p>

                  <p
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    Votre colis sera livré par la société de livraison best delivery
                    <img
                      src={deliveryLogo}
                      alt="best delivery"
                      width={"100px"}
                      style={{ marginTop: "-10px" }}
                    />
                  </p>

                  <br />

                  <p style={{ marginTop: "-10px" }}>
                    <span>
                      <strong>méthode de paiement :</strong> paiement cash à la livraison
                    </span>
                    <p>
                      <span>
                        <strong>détail de paiement : </strong>
                      </span>{" "}
                      <span>total :{infos.totalTTC} DT </span>
                    </p>
                  </p>
                </div>
              ))}

            <a href="/collections/électroménager%20&%20cuisine">
              <Button
                endIcon={<ShoppingCartCheckoutIcon />}
                color="warning"
                variant="contained"
                size="small"
                sx={{ marginTop: 2 }}
              >
                continuer VOS ACHATS
              </Button>
            </a>
          </div>
        ) : (
          <div className="panierVide appleGlassWhite fadeInBlur">
            <div className="svgContainer">
              <svg className="cartSvg" width="130" height="130" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 10L12 3L17 10"
                  stroke="black"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
                <rect x="3" y="10" width="18" height="11" rx="2" stroke="black" strokeWidth="1.4" />
                <circle cx="9" cy="15" r="1.4" fill="black" />
                <circle cx="15" cy="15" r="1.4" fill="black" />
              </svg>
            </div>

            <h2>Votre panier est vide</h2>
            <p>Découvrez nos produits et trouvez vos favoris.</p>

            <a href="/collections/électroménager%20&%20cuisine">
              <div className="appleBtnWhite">
                <span>Commencer</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12H19M19 12L12 5M19 12L12 19"
                    stroke="black"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </a>
          </div>
        )
      ) : (
        <div className="cart">
          <div className="panier">
            <h2 className="panierTitle">PANIER ({panierQte})</h2>

            <ul style={{ width: "90%" }} id="commande">
              {cart.map((article, index) => (
                <li key={index} className="allDetail">
                  <div className="deleteDetail">
                    <div className="pictureName">
                      {article.family === "promos" ? (
                        <img
                          src={require(`../../images/promos/${article.img}.jpg`)}
                          alt="produit"
                          className="prodPic"
                        />
                      ) : 


article.subSubcategorie ? (
  <a
    href={`/produit/${slugify(article.family)}/${slugify(
      article.categorie
    )}/${slugify(article.souscategorie)}/${slugify(
      article.subSubcategorie
    )}/${slugify(article.name)}`}
  >
    <img
      src={`https://firebasestorage.googleapis.com/v0/b/ebuy-10d2e.appspot.com/o/${decodeURIComponent(
        article.family
      )}%2F${decodeURIComponent(article.categorie)}%2F${decodeURIComponent(
        article.souscategorie
      )}%2F${decodeURIComponent(article.subSubcategorie)}%2F${decodeURIComponent(
        article.subSubcategorie
      )}%20${decodeURIComponent(article.name)}.jpeg?alt=media`}
      alt={article.name}
      className="prodPic"
    />
  </a>
) : article.soussouscategorie ? (
  <a
    href={`/produit/${slugify(article.family)}/${slugify(
      article.categorie
    )}/${slugify(article.souscategorie)}/${slugify(
      article.soussouscategorie
    )}/${slugify(article.name)}`}
  >
    <img
      src={`https://firebasestorage.googleapis.com/v0/b/ebuy-10d2e.appspot.com/o/${decodeURIComponent(
        article.family
      )}%2F${decodeURIComponent(article.categorie)}%2F${decodeURIComponent(
        article.souscategorie
      )}%2F${decodeURIComponent(article.soussouscategorie)}%2F${decodeURIComponent(
        article.soussouscategorie
      )}%20${decodeURIComponent(article.name)}.jpeg?alt=media`}
      alt={article.name}
      className="prodPic"
    />
  </a>
) : article.souscategorie ? (
  <a
    href={`/produit/${slugify(article.family)}/${slugify(
      article.categorie
    )}/${slugify(article.souscategorie)}/${slugify(article.name)}`}
  >
    <img
      src={`https://firebasestorage.googleapis.com/v0/b/ebuy-10d2e.appspot.com/o/${decodeURIComponent(
        article.family
      )}%2F${decodeURIComponent(article.categorie)}%2F${decodeURIComponent(
        article.souscategorie
      )}%2F${decodeURIComponent(article.souscategorie)}%20${decodeURIComponent(
        article.name
      )}.jpeg?alt=media`}
      alt={article.name}
      className="prodPic"
    />
  </a>
) : (
  <a
    href={`/produit/${slugify(article.family)}/${slugify(
      article.categorie
    )}/${slugify(article.name)}`}
  >
    <img
      src={`https://firebasestorage.googleapis.com/v0/b/ebuy-10d2e.appspot.com/o/${decodeURIComponent(
        article.family
      )}%2F${decodeURIComponent(article.categorie)}%2F${decodeURIComponent(
        article.img
      )}.jpeg?alt=media`}
      alt="produit"
      className="prodPic"
    />
  </a>

                      )}

                      <div className="nameBrand">
                        <span>
                          {safeInt(article.quantite)}* {article.name}
                        </span>
                        <span>{article.brand}</span>
                        {!!article.reference && (
                          <span style={{ fontSize: "9px", opacity: 0.7 }}>
                            ref: {article.reference}
                          </span>
                        )}
                        {!!article.color && (
    <div className="cartItemColorDiv">
      <span style={{ fontSize: "11px", opacity: 0.8, textTransform: "capitalize" }}>
        {article.color}
      </span>
      <img
        src={require(`../../images/colors/${article.color}.jpeg`)}
        alt={article.color}
        className="cartColorCircle"
      />
    </div>
  )}
                      </div>
                    </div>

                    <Button
                      color="warning"
                      sx={{ marginTop: "10px" }}
                      endIcon={<DeleteIcon />}
                      onClick={() => deleteArticle(index)}
                    >
                      SUPPRIMER
                    </Button>
                  </div>

                  <div className="prodDetail">
                    <div className="priceDiv">
                      {((article.price || 0) * safeInt(article.quantite)).toFixed(3)}&nbsp;TND
                    </div>

                    <div className="artcileDetails">
                      <div className="quantity">
                        <div className="plusmoin" onClick={() => moin(article, index)}>
                          <RemoveCircleIcon color="warning" fontSize="medium" sx={{ cursor: "pointer" }} />
                        </div>
                        <div className="numberArticle">{safeInt(article.quantite)}</div>
                        <div className="plusmoin" id="plusMoin" onClick={() => plus(article)}>
                          <AddCircleIcon color="warning" fontSize="medium" sx={{ cursor: "pointer" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="totalDiv">
            <h5>RÉSUMÉ DU PANIER</h5>

            <p id="total">
              <span>Sous-Total TTC:&nbsp;&nbsp;</span>
              <span>{calculerTotal().toFixed(3)}&nbsp;TND</span>
            </p>

         

            {calculerRemiseHello5() > 0 && (
              <p style={{ color: "#F77171", fontWeight: "bold" }}>
                Code HELLO-5 : -{calculerRemiseHello5().toFixed(3)} TND
              </p>
            )}

            <p className="livraison">
              <span>frais de livraison:&nbsp;&nbsp;</span>
              <span>{shippingCost === 0 ? "Gratuite" : `${shippingCost} TND`}</span>
            </p>

            <p id="totalTTC" className="totalTTC">
              <span>Total TTC:&nbsp;&nbsp;</span>
              <span>{calculerTotalFinalAvecLivraison().toFixed(3)}&nbsp;TND</span>
            </p>

            <div className="livraisonDomicile">
              <p>
                <Button sx={{ color: "black" }} startIcon={<LocalShippingIcon />}>
                  Livraison à domicile :&nbsp;&nbsp;
                </Button>
              </p>

              <p style={{ marginTop: "-15px" }}>
                <Box sx={{ display: "flex", marginTop: "15px" }}>
                  <PaymentsIcon fontSize="small" />
                  Méthode de paiement: en espèces au moment de la livraison
                </Box>
              </p>
            </div>

            <form
              className="validForm"
              onSubmit={(event) => {
                event.preventDefault();
                passeCommande();
              }}
            >
              <TextField
                autoFocus
                required
                error={nameError !== ""}
                helperText={nameError}
                margin="dense"
                label="Nom et Prenom"
                type="text"
                fullWidth
                variant="standard"
                value={nameValue}
                onChange={handleNameChange}
                InputLabelProps={{
                  sx: { color: "black", fontFamily: "Exo", fontSize: "14px" },
                }}
                InputProps={{
                  sx: { color: "black", fontFamily: "Exo", fontSize: "14px" },
                }}
              />

              <TextField
                required
                error={adressError !== ""}
                helperText={adressError}
                margin="dense"
                label="Adresse Livraison"
                type="text"
                fullWidth
                variant="standard"
                value={adressValue}
                onChange={handleAdressChange}
                InputLabelProps={{
                  sx: { color: "black", fontFamily: "Exo", fontSize: "14px" },
                }}
                InputProps={{
                  sx: { color: "black", fontFamily: "Exo", fontSize: "14px" },
                }}
              />

              <TextField
                required
                error={numberError !== ""}
                helperText={numberError}
                margin="dense"
                label="Numero Telephone"
                type="text"
                fullWidth
                variant="standard"
                value={NumberValue}
                onChange={handleNumberChange}
                InputLabelProps={{
                  sx: { color: "black", fontFamily: "Exo", fontSize: "14px" },
                }}
                InputProps={{
                  sx: { color: "black", fontFamily: "Exo", fontSize: "14px" },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                color="warning"
                size="medium"
                sx={{ marginTop: "20px" }}
              >
                passer la commande
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}